import type { CoreBadgeTone } from '@garnet/ui';
import type {
  AdminSource,
  CircuitState,
  CredentialState,
  SupportGrant,
  WebhookDelivery,
} from './types.js';

/**
 * Pure data-shaping for the admin console — no React, no I/O, no clock (CONVENTIONS).
 * Unit-tested directly in shape.test.ts. The health derivations, the promote gate, the
 * support-grant window and the webhook replay/dead-letter rules all live here so they are
 * provable without a renderer. A caller injects `now` where time matters (Invariant I1).
 */

// ── Source health (PRD §7, §20) ──────────────────────────────────────────────
/** Fraction of the freshness window consumed; >1 means the source is stale. */
export function freshnessRatio(ageSeconds: number, windowSeconds: number): number {
  if (windowSeconds <= 0) return Infinity;
  return ageSeconds / windowSeconds;
}

export function isStale(ageSeconds: number, windowSeconds: number): boolean {
  return freshnessRatio(ageSeconds, windowSeconds) > 1;
}

/** Quota consumed today as a 0–100 percentage; an unmetered (null limit) source is 0. */
export function quotaPct(usedToday: number, limitPerDay: number | null): number {
  if (limitPerDay === null || limitPerDay <= 0) return 0;
  return Math.min(100, Math.round((usedToday / limitPerDay) * 100));
}

/**
 * Promote gate (PRD §20): a `planned` source may be promoted to `live` only once it is
 * actually connected. Live/degraded/down sources are not re-promotable.
 */
export function canPromote(source: AdminSource): boolean {
  return source.status === 'planned' && source.connection === 'connected';
}

export interface HealthVerdict {
  tone: CoreBadgeTone;
  label: string;
}

/**
 * Overall per-source health, worst-signal-first: an open breaker or a down/unreachable
 * source fails; a tripping breaker, degraded status or stale feed warns; otherwise healthy.
 */
export function sourceHealth(source: AdminSource): HealthVerdict {
  const { status, health, freshness_window_seconds } = source;
  const stale = isStale(health.last_success_age_seconds, freshness_window_seconds);
  if (status === 'planned') return { tone: 'b-neutral', label: 'planned' };
  if (status === 'down' || !health.reachable || health.circuit === 'open') return { tone: 'b-fail', label: 'unhealthy' };
  if (status === 'degraded' || health.circuit === 'half_open' || stale) return { tone: 'b-warn', label: 'degraded' };
  return { tone: 'b-pass', label: 'healthy' };
}

export function statusTone(status: AdminSource['status']): CoreBadgeTone {
  switch (status) {
    case 'live':
      return 'b-pass';
    case 'degraded':
      return 'b-warn';
    case 'down':
      return 'b-fail';
    case 'planned':
      return 'b-neutral';
  }
}

export function circuitTone(circuit: CircuitState): CoreBadgeTone {
  switch (circuit) {
    case 'closed':
      return 'b-pass';
    case 'half_open':
      return 'b-warn';
    case 'open':
      return 'b-fail';
  }
}

export function credentialTone(state: CredentialState): CoreBadgeTone {
  switch (state) {
    case 'valid':
      return 'b-pass';
    case 'expiring':
      return 'b-warn';
    case 'expired':
      return 'b-fail';
  }
}

// ── Support grants (PRD §20) ─────────────────────────────────────────────────
/** A grant reaches customer content only while active and inside its time box. */
export function isGrantActive(grant: SupportGrant, nowIso: string): boolean {
  return grant.status === 'active' && Date.parse(grant.expires_at) > Date.parse(nowIso);
}

/** Whole minutes remaining in a grant's time box (0 once expired). */
export function grantMinutesRemaining(grant: SupportGrant, nowIso: string): number {
  const ms = Date.parse(grant.expires_at) - Date.parse(nowIso);
  return ms <= 0 ? 0 : Math.floor(ms / 60000);
}

// ── Webhook ops (PRD §15, §20) ───────────────────────────────────────────────
/** A delivery is dead-lettered once it has exhausted its attempts without delivering. */
export function isDeadLetter(delivery: WebhookDelivery): boolean {
  return delivery.status === 'dead_letter' || (delivery.status === 'failed' && delivery.attempts >= delivery.max_attempts);
}

/** Only a failed or dead-lettered delivery can be replayed; delivered/pending cannot. */
export function canReplay(delivery: WebhookDelivery): boolean {
  return delivery.status === 'failed' || delivery.status === 'dead_letter';
}

export function webhookTone(status: WebhookDelivery['status']): CoreBadgeTone {
  switch (status) {
    case 'delivered':
      return 'b-pass';
    case 'pending':
      return 'b-info';
    case 'failed':
      return 'b-warn';
    case 'dead_letter':
      return 'b-fail';
  }
}
